/**
 * Data và Thuật toán Tính toán Chẩn đoán Sức Khỏe Nhà Thuốc (Radar Sức Khỏe Quầy Thuốc)
 * Dựa trên mô hình tài chính và thực tế vận hành bán lẻ dược phẩm Việt Nam.
 */

// Giá trị mặc định ban đầu
export const DEFAULT_HEALTH_INPUTS = {
  ds: 7,       // Doanh số trung bình 1 ngày (triệu đ)
  bien: 21,    // Biên lãi gộp trung bình (%)
  dongia: 85,  // Đơn giá trung bình mỗi khách (nghìn đ)
  mb: 10,      // Thuê mặt bằng 1 tháng (triệu đ)
  khac: 3,     // Chi phí cố định khác 1 tháng (triệu đ)
  ns: 18,      // Lương + thưởng nhân sự 1 tháng (triệu đ - tính cả công chủ quầy)
  ton: 420,    // Giá trị hàng tồn kho hiện tại (triệu đ)
  
  // 5 Chỉ số mở rộng (Nâng cao)
  nhansu: 3,   // Số người làm tại quầy (tính cả chủ quầy)
  huy: 0.8,    // Hàng cận date, hỏng, hủy mỗi tháng (% doanh thu)
  vay: 0,      // Lãi vay phải trả mỗi tháng (triệu đ)
  vitri: 'dancu', // Vị trí quầy
  namhd: 4,    // Quầy hoạt động được (năm)
};

// Giới hạn hợp lệ cho từng ô nhập số - chặn số âm và số phi thực tế (vd gõ nhầm thêm số 0),
// đồng thời khớp đúng với các mức clamp đã dùng trong computeHealthMetrics() bên dưới
// (bien: 0-90, huy: 0-20) để input hiển thị và số thực tế dùng để tính toán không bị lệch nhau.
export const INPUT_LIMITS = {
  ds: { min: 0, max: 500 }, // triệu đ/ngày - chip cao nhất 20tr, cho phép rộng tới quầy rất lớn
  bien: { min: 0, max: 90 }, // %
  dongia: { min: 1, max: 5000 }, // nghìn đ - chip cao nhất 150k
  mb: { min: 0, max: 500 }, // triệu đ/tháng - chip cao nhất 30tr
  khac: { min: 0, max: 200 }, // triệu đ/tháng
  ns: { min: 0, max: 500 }, // triệu đ/tháng
  ton: { min: 0, max: 10000 }, // triệu đ - chip cao nhất 800tr, cho phép tới quy mô chuỗi
  nhansu: { min: 1, max: 100 }, // người
  huy: { min: 0, max: 20 }, // % doanh thu
  vay: { min: 0, max: 500 }, // triệu đ/tháng
};

// Gợi ý bấm nhanh (Chips)
export const INPUT_PRESET_CHIPS = {
  ds: [
    { label: '3 tr', value: 3 },
    { label: '5 tr', value: 5 },
    { label: '8 tr', value: 8 },
    { label: '12 tr', value: 12 },
    { label: '20 tr', value: 20 },
  ],
  dongia: [
    { label: '50k', value: 50 },
    { label: '70k', value: 70 },
    { label: '100k', value: 100 },
    { label: '150k', value: 150 },
  ],
  mb: [
    { label: 'Nhà mình (0đ)', value: 0 },
    { label: '5 tr', value: 5 },
    { label: '10 tr', value: 10 },
    { label: '18 tr', value: 18 },
    { label: '30 tr', value: 30 },
  ],
  ton: [
    { label: '150 tr', value: 150 },
    { label: '300 tr', value: 300 },
    { label: '500 tr', value: 500 },
    { label: '800 tr', value: 800 },
  ],
};

// Vị trí quầy options
export const LOCATION_OPTIONS = [
  { value: 'dancu', label: 'Khu dân cư / Trong ngõ' },
  { value: 'matpho', label: 'Mặt phố lớn / Trục đường chính' },
  { value: 'cho', label: 'Gần chợ / Trung tâm thương mại' },
  { value: 'benhvien', label: 'Gần bệnh viện / Phòng khám đa khoa' },
  { value: 'nongthon', label: 'Nông thôn / Thị trấn' },
];

// Định nghĩa 5 mức xếp hạng sức khỏe
export const HEALTH_BANDS = [
  {
    min: 82,
    cls: 'b-good',
    badgeColor: '#10b981',
    tag: 'Quầy khỏe',
    title: 'Quầy đang tạo ra lợi nhuận thật',
    sub: 'Cấu trúc tài chính vững vàng. Việc còn lại là giữ nhịp và tìm dư địa tăng trưởng mở rộng, không phải chữa cháy.',
  },
  {
    min: 68,
    cls: 'b-good',
    badgeColor: '#00754a',
    tag: 'Khá — có điểm nghẽn',
    title: 'Quầy ổn, nhưng đang có chỗ rò rỉ tiền',
    sub: 'Nền tảng vận hành tốt. Xử lý đúng một hoặc hai điểm nghẽn bên dưới là lợi nhuận bật lên ngay trong quý tới.',
  },
  {
    min: 52,
    cls: 'b-warn',
    badgeColor: '#f59e0b',
    tag: 'Cảnh báo',
    title: 'Lãi mỏng — quầy dễ bị tổn thương',
    sub: 'Quầy vẫn chạy nhưng đệm an toàn thấp. Một tháng vắng khách hoặc một đợt tăng tiền thuê là đủ để rơi vào trạng thái lỗ.',
  },
  {
    min: 36,
    cls: 'b-bad',
    badgeColor: '#f97316',
    tag: 'Nguy hiểm',
    title: 'Quầy đang âm thầm bào mòn vốn',
    sub: 'Doanh số có thể vẫn đều, nhưng tiền không ở lại trong két. Cần cắt giảm chi phí và giải phóng tồn kho ngay tháng này.',
  },
  {
    min: 0,
    cls: 'b-bad',
    badgeColor: '#ef4444',
    tag: 'Báo động đỏ',
    title: 'Cần tái cấu trúc, không phải tối ưu nhỏ',
    sub: 'Với cấu trúc hiện tại, quầy càng chạy càng lỗ. Phải thay đổi căn bản về chi phí, biên lãi gộp hoặc địa điểm mặt bằng.',
  },
];

// Thư viện 10 chân dung quầy thuốc (P0 đến P9)
export const PORTRAITS = [
  {
    id: 'P0',
    ten: 'Chưa đủ dữ liệu để chấm',
    tagline: 'Bạn chưa tính công của chính mình',
    mota: 'Chi phí nhân sự đang để 0 nên mọi con số lợi nhuận bên dưới đều là con số ảo.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Công đứng quầy của bạn là chi phí thật. Nếu không tính vào, quầy nào cũng trông như đang có lãi. Rất nhiều chủ quầy làm 10 năm mới nhận ra mình đang tự trả lương cho mình bằng chính vốn của mình.',
        b: [
          'Hãy nhập mức lương bạn phải trả nếu thuê một dược sĩ khác làm đúng phần việc bạn đang làm',
          'Nếu vợ chồng cùng đứng quầy, hãy tính công xứng đáng cho cả hai',
          'Nhập xong hãy xem lại bài kiểm tra để có kết quả thực tế',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Khảo sát giá thuê dược sĩ đứng quầy tại khu vực của bạn, lấy con số đó làm mức lương sàn của chính mình',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Tách bạch tiền quầy và tiền cá nhân bằng hai tài khoản riêng biệt, tự chuyển trả lương cho mình đều đặn hàng tháng',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: ['Cần cập nhật số liệu chi phí đầy đủ để đánh giá chính xác.'],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: ['Doanh thu ngày', 'Lãi gộp ngày', 'Tiền mặt thực tế cuối ngày'],
      },
    ],
  },
  {
    id: 'P1',
    ten: 'Quầy Gánh Mặt Bằng',
    tagline: 'Bán được nhưng lãi chảy hết vào tiền nhà',
    mota: 'Tiền thuê đang chiếm {tyMB} doanh thu — ngưỡng an toàn của quầy lẻ là dưới 6%.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Tiền thuê chiếm {tyMB} doanh thu, nghĩa là gần trọn tuần đầu mỗi tháng bạn mở cửa chỉ để trả tiền nhà. Đây là loại chi phí không co giãn: doanh số xuống nó vẫn giữ nguyên, nên nó quyết định phần lớn rủi ro sống còn của quầy.',
        b: [
          'Thuê giá cao kỳ vọng lưu lượng lớn nhưng lưu lượng thực tế không tới như dự tính',
          'Diện tích thuê quá rộng so với nhu cầu thực của một quầy thuốc',
          'Hợp đồng ký lúc thị trường nóng, đã vài năm chưa đàm phán lại dù mặt bằng xung quanh đã giảm',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Rà soát hợp đồng thuê, ghi rõ ngày hết hạn và điều khoản tăng giá để biết mình còn bao nhiêu thời gian',
          'Tính doanh thu trên mỗi m²: lấy doanh thu tháng chia diện tích. Quầy khỏe thường đạt 8–15 triệu/m²/tháng',
          'Khoanh vùng diện tích không sinh tiền: kho để lộ, lối đi thừa, kệ trống, khu vực khách không bao giờ bước tới',
          'Đếm lượt khách theo từng khung 2 tiếng trong 3 ngày liên tục để biết khung giờ nào bị chết lưu lượng',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Chuẩn bị hồ sơ đàm phán với chủ nhà: thời gian thuê, giá mặt bằng trống xung quanh, đề xuất giữ giá hoặc giảm 10–15% đổi lấy cam kết gia hạn dài hạn',
          'Nếu chủ nhà không giảm, tính bài toán di dời trong bán kính 300m: chi phí chuyển cộng mất khách 2–3 tháng so với phần tiết kiệm được trong 12 tháng',
          'Nếu không chuyển được, lấp diện tích thừa bằng nhóm hàng biên cao: dược mỹ phẩm, thực phẩm chức năng, dụng cụ y tế, sữa chuyên biệt',
          'Thêm dịch vụ tại chỗ để tăng doanh thu trên cùng mặt bằng: đo huyết áp, kiểm tra đường huyết, tư vấn theo lịch hẹn',
          'Cân nhắc mở sớm hoặc đóng muộn hơn nếu khu vực có nhu cầu — cùng một tiền thuê nhưng thêm thời gian khai thác',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: 'Khi tiền thuê cao, mỗi mét kệ phải làm việc nhiều hơn bình thường.',
        b: [
          'Quy tắc vàng: mỗi mét kệ phải tạo ra ít nhất 1 triệu lãi gộp mỗi tháng. Mã nào không đạt thì cắt giảm',
          'Ưu tiên nhóm biên cao và quay nhanh trên từng mét kệ trưng bày, bỏ hàng cồng kềnh quay chậm',
          'Đàm phán với nhà phân phối giao 2 lần mỗi tuần theo lô nhỏ, tránh trữ hàng chiếm diện tích bán',
          'Đưa hàng bán kèm ra khu vực thanh toán — vị trí này luôn là mét vuông sinh lời cao nhất quầy',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Doanh thu trên mỗi m² mỗi tháng — mục tiêu từ 10 triệu trở lên',
          'Tỷ lệ tiền thuê trên doanh thu — mục tiêu đưa về dưới 6%',
          'Doanh thu theo khung giờ — tìm và lấp đầy khung giờ vắng khách',
        ],
      },
    ],
  },
  {
    id: 'P2',
    ten: 'Quầy Ôm Kho',
    tagline: 'Tiền của bạn đang nằm trên kệ, không nằm trong két',
    mota: 'Kho đủ bán {ngayTon} — mức lành mạnh là 45–65 ngày. Khoảng {tienChet} đang bị giam chết.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Kho của bạn đủ bán {ngayTon}, trong khi mức lành mạnh của quầy lẻ là 45–65 ngày. Phần dư khoảng {tienChet} vừa là tiền mặt bị giam cứng, vừa là nguy cơ hàng cận date của 6–12 tháng tới. Đây là kiểu quầy nhìn sổ sách tưởng có lãi nhưng trong két không bao giờ có tiền mặt.',
        b: [
          'Nhập theo chương trình chiết khấu ôm hàng mà không tính sức bán thực tế của mã đó',
          'Nhập theo cảm tính "để khách hỏi là có", dẫn đến hàng trăm mã bán cực kỳ chậm',
          'Thiếu người phụ trách kiểm date định kỳ, đến lúc phát hiện thì hàng đã sát hạn dùng',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Xuất báo cáo tồn kho từ phần mềm, sắp xếp theo số ngày không phát sinh đơn bán',
          'Phân loại làm 3 nhóm: hàng chết trên 120 ngày, hàng chậm 60–120 ngày, hàng cận date dưới 6 tháng',
          'Liên hệ nhà cung cấp hỏi chính sách đổi trả hàng còn hạn dài — nhiều hãng hỗ trợ nếu hạn còn xa',
          'Dừng ngay toàn bộ đơn nhập mới đối với các mã thuộc nhóm chết và chậm',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Xả hàng tồn theo thứ tự ưu tiên: bán combo kèm hàng chạy, giảm giá bán thu hồi vốn, cuối cùng mới thanh lý hoặc trả nhà cung cấp',
          'Đặt hạn mức nhập cho từng mã bằng đúng sức bán 45 ngày. Kiên quyết không phá hạn mức chỉ vì chiết khấu',
          'Lập danh sách 50 mã cốt lõi tạo ra 80% doanh thu, tập trung vốn ưu tiên cho nhóm này',
          'Cố định lịch kiểm date ngày mùng 5 hàng tháng, giao cụ thể cho một nhân sự chịu trách nhiệm',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: 'Chuyển đổi từ tư duy nhập nhiều cho rẻ sang nhập đơn nhỏ và giao hàng thường xuyên.',
        b: [
          'Áp dụng quy tắc ABC: nhóm A luôn đủ hàng, nhóm C chỉ giữ một hộp mẫu, hết thì nhận đặt theo yêu cầu khách',
          'Chênh lệch chiết khấu 1–2% không đủ bù tiền vốn bị giam cộng nguy cơ hàng hỏng cận date',
          'Với các chương trình khuyến mãi, chỉ nhận khi tổng số lượng nằm trong sức bán tối đa 60 ngày',
          'Ghi ngày nhập lên từng thùng để nhìn là biết hàng nằm bao lâu, không cần mở phần mềm tra cứu',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Số ngày tồn kho — mục tiêu đưa về 55–65 ngày trong vòng 3 tháng',
          'Giá trị hàng cận date dưới 6 tháng — giảm liên tục từng tuần',
          'Số mã tồn kho trên 120 ngày không phát sinh đơn — mục tiêu đưa về 0',
        ],
      },
    ],
  },
  {
    id: 'P3',
    ten: 'Quầy Biên Mỏng',
    tagline: 'Bán nhiều nhưng lãi trên mỗi đơn quá thấp',
    mota: 'Biên lãi gộp {bien} — quầy vận hành ổn định thường ở mức 20–26%.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Biên lãi gộp của bạn là {bien}, trong khi quầy lẻ vận hành ổn định thường đạt 20–26%. Mỗi 100.000đ bán ra bạn chỉ giữ lại {bienDon}đ để trả tiền thuê nhà, lương nhân viên, điện nước và lợi nhuận. Bán thêm nhiều đơn ở mức biên này rất khó cải thiện tình hình — bắt buộc phải tái cơ cấu danh mục sản phẩm.',
        b: [
          'Cơ cấu hàng nghiêng quá nhiều về thuốc phổ thông ai cũng bán, buộc phải cạnh tranh khốc liệt bằng giá',
          'Giá nhập chưa tối ưu do đơn hàng nhỏ lẻ, phân tán quá nhiều đầu mối nên không lên được bậc chiết khấu',
          'Giảm giá tùy tiện cho người quen mà không kiểm soát, mỗi lần vài phần trăm nhưng cộng lại bào mòn hết lãi',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'In danh sách 30 mã bán chạy nhất, ghi rõ: giá nhập, giá bán, biên lãi %. Đánh dấu đỏ các mã có biên dưới 12%',
          'Rà soát lại chính sách giảm giá đang áp dụng tại quầy: ai được giảm, giảm bao nhiêu %, ai duyệt',
          'Liệt kê tất cả các đầu mối đang nhập hàng và số tiền chi cho từng bên mỗi tháng',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Gom đơn về 1–2 nhà phân phối chiến lược lớn để đạt hạn mức chiết khấu cao hơn, thay vì rải nhỏ khắp nơi',
          'Thiết kế bộ bán kèm cho 10 nhóm bệnh thường gặp nhất, mỗi nhóm kèm sản phẩm hỗ trợ biên cao hợp lý về chuyên môn',
          'Đưa vào 5–10 mã độc quyền hoặc hàng ít bị so sánh giá, biên thường đạt 35–45%',
          'Ban hành quy chế giảm giá bằng văn bản rõ ràng: mã nào được giảm, tối đa bao nhiêu %, thẩm quyền duyệt',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: 'Mục tiêu cơ cấu doanh thu chuẩn: 60% thuốc phổ thông để giữ chân khách, 25% thực phẩm chức năng & dược mỹ phẩm tạo lãi, 15% hàng độc quyền tránh so giá.',
        b: [
          'Không cạnh tranh giá ở nhóm hàng phổ thông ai cũng có — cạnh tranh bằng tư vấn chuyên môn và luôn đủ hàng',
          'Với hàng độc quyền, trao đổi với nhà phân phối về chính sách khu vực bảo hộ nếu có',
          'Mỗi tháng thay thế 1 mã biên thấp bằng 1 mã biên cao có cùng công dụng và hiệu quả tương đương',
          'Tăng biên lãi gộp thêm 1 điểm % mang lại giá trị lớn hơn nhiều nỗ lực tăng doanh số, mà không tốn thêm chi phí cố định',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Biên lãi gộp trung bình — mục tiêu tăng 1 điểm % mỗi tháng, đạt 22% trong 4 tháng',
          'Tỷ trọng doanh thu từ thực phẩm bảo vệ sức khỏe và dược mỹ phẩm — mục tiêu từ 25% trở lên',
          'Tỷ lệ đơn có bán kèm trên tổng số đơn bán — mục tiêu từ 30% trở lên',
        ],
      },
    ],
  },
  {
    id: 'P4',
    ten: 'Quầy Đông Người, Năng Suất Thấp',
    tagline: 'Chi phí nhân sự chạy nhanh hơn tốc độ tăng doanh thu',
    mota: 'Mỗi người đang tạo ra {dtNS} doanh thu/tháng, chi phí nhân sự chiếm {tyNS} doanh thu.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Mỗi nhân sự tại quầy đang tạo ra {dtNS} doanh thu mỗi tháng, trong khi quầy vận hành hiệu quả đạt từ 120–180 triệu/người. Chi phí nhân sự chiếm {tyNS} doanh thu, ngưỡng khỏe là dưới 10%. Vấn đề hiếm khi do nhân viên lười — thường do lịch trực không khớp giờ cao điểm hoặc nhân viên chỉ bán đúng món khách hỏi.',
        b: [
          'Lịch ca trực xếp theo thói quen thay vì theo lưu lượng khách hàng thực tế',
          'Nhân sự chưa được đào tạo kỹ năng tư vấn cắt liều và bán kèm liệu trình, chỉ đóng vai trò lấy hàng',
          'Chưa có chỉ tiêu rõ ràng (KPI) nên nhân sự không biết mình đang làm tốt hay chưa tốt',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Ghi nhận doanh thu theo từng khung 2 tiếng trong 5 ngày liên tục để vẽ biểu đồ giờ cao điểm và giờ thấp điểm',
          'Đối chiếu lịch trực hiện tại với khung giờ đông khách — xem có đang thừa người giờ vắng và thiếu người giờ đông',
          'Thống kê đơn giá trung bình theo từng dược sĩ bán hàng để xác định tiềm năng cải thiện kỹ năng',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Bố trí lại ca trực bám sát lưu lượng khách thực tế trong tuần',
          'Giao chỉ tiêu đơn giá trung bình cho từng ca làm việc, dán bảng mục tiêu cạnh quầy thu ngân',
          'Đào tạo chuyên môn 30 phút mỗi tuần về 1 nhóm bệnh, thực hành tình huống bán kèm',
          'Gắn cơ chế thưởng theo lãi gộp tạo ra thay vì chỉ theo doanh thu thuần',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Chuẩn hóa 10 kịch bản tư vấn theo nhóm bệnh thường gặp, in thành sổ tay để nhân sự mới học nhanh',
          'Bày trí hàng bán kèm ngay trong tầm với của dược sĩ bán hàng, không cất kỹ trong tủ kín',
          'Soạn sẵn câu gợi ý mở lời cho từng tình huống để nhân viên tự tin tư vấn',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Doanh thu trên mỗi nhân sự mỗi tháng — mục tiêu từ 120 triệu trở lên',
          'Đơn giá trung bình theo từng ca trực',
          'Tỷ lệ chi phí nhân sự trên tổng doanh thu — mục tiêu dưới 10%',
        ],
      },
    ],
  },
  {
    id: 'P5',
    ten: 'Quầy Đơn Giá Thấp',
    tagline: 'Khách vẫn tới, nhưng rời quầy với quá ít giá trị',
    mota: 'Đơn giá trung bình chỉ {dongia}, khoảng {luot} lượt khách mỗi ngày.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Đơn giá trung bình của bạn là {dongia} với khoảng {luot} lượt khách mỗi ngày. Lưu lượng khách tương đối tốt — điểm nghẽn là mỗi khách rời đi với quá ít giá trị. Nâng đơn giá luôn nhanh và ít tốn kém hơn kéo khách mới, vì khách đã có niềm tin và đang đứng trước quầy của bạn.',
        b: [
          'Chỉ bán đúng món khách yêu cầu thay vì hỏi thêm các triệu chứng liên quan',
          'Bán lẻ vài liều/vỉ theo yêu cầu thay vì tư vấn đủ liệu trình điều trị',
          'Quầy thiếu các dòng sản phẩm có giá trị gia tăng để bán kèm',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Tự quan sát 20 lượt bán hàng liên tiếp: có bao nhiêu lượt bạn chủ động hỏi thêm về triệu chứng hoặc người nhà cùng dùng',
          'Lập danh sách 10 lý do phổ biến nhất khiến khách tìm đến quầy của bạn',
          'Với mỗi lý do, liệt kê 1 sản phẩm bán kèm hợp lý về mặt chuyên môn dược lý',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Chuyển từ tư duy bán theo vỉ sang tư vấn đủ liệu trình điều trị, giải thích rõ lý do cần tuân thủ đủ ngày',
          'Thiết lập 3 combo giải pháp cố định: combo cảm sốt nâng cao sức đề kháng, combo tiêu hóa dạ dày, combo xương khớp',
          'Bày trí các sản phẩm giá trị nhỏ mua thêm ngay cạnh vị trí tính tiền: vitamin C, men vi sinh, xịt họng, nước muối sinh lý',
          'Đặt mục tiêu đơn giá tối thiểu cho từng ngày, tổng kết sau mỗi ca',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Bổ sung nhóm sản phẩm giá trị cao có chu kỳ mua nhắc lại: thực phẩm bảo vệ sức khỏe liệu trình 1–3 tháng, sữa y tế, dược mỹ phẩm',
          'Với bệnh mạn tính, tư vấn mua liệu trình theo tháng thay vì theo tuần để khách có mức giá ưu đãi và hiệu quả ổn định',
          'Chuẩn bị 3 phương án giá cho mỗi vấn đề bệnh lý để khách hàng tự lựa chọn phương án tối ưu cho mình',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Đơn giá trung bình — mục tiêu tăng thêm 10.000đ – 15.000đ trong 30 ngày',
          'Số lượng sản phẩm trung bình trên một hóa đơn — mục tiêu từ 2.2 sản phẩm trở lên',
          'Tỷ lệ hóa đơn có từ 2 sản phẩm trở lên',
        ],
      },
    ],
  },
  {
    id: 'P6',
    ten: 'Quầy Vắng Khách',
    tagline: 'Vấn đề nằm ở đầu vào phễu, không nằm ở giá bán',
    mota: 'Chỉ khoảng {luot} lượt khách mỗi ngày — quầy sống khỏe thường cần từ 50 lượt trở lên.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Quầy bạn chỉ có khoảng {luot} lượt khách mỗi ngày, trong khi mức để quầy sống khỏe thường từ 50 lượt trở lên. Vấn đề nằm ở lưu lượng đầu vào: khách hàng trong khu vực không biết bạn ở đó, hoặc biết nhưng chưa có lý do để ghé. Giảm giá lúc này là sai lầm vì vừa phá biên lãi vừa không tạo thêm khách mới.',
        b: [
          'Quầy chưa được định vị đầy đủ và chuyên nghiệp trên Google Maps',
          'Biển hiệu mờ, khuất tầm nhìn, ánh sáng yếu hoặc cửa kính đóng kín tạo cảm giác xa cách',
          'Chưa tạo dựng được mối liên kết với cộng đồng dân cư xung quanh ngoài lúc có bệnh',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Mở điện thoại tìm vị trí quầy trên Google Maps. Nếu chưa có hãy xác minh ngay; nếu có hãy cập nhật đầy đủ hình ảnh, số hotline, giờ mở cửa',
          'Đứng đối diện quầy 10 phút vào giờ tan tầm: biển hiệu có đọc rõ từ cự ly 20m không, quầy có sáng sủa và dễ bước vào không',
          'Lập tài khoản Zalo OA hoặc Zalo quầy chuẩn chỉ, ghi rõ thông tin phục vụ và tư vấn từ xa',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Tham gia 3–5 nhóm cư dân khu phố hoặc chợ mạng trong bán kính 2km, hỗ trợ trả lời các thắc mắc về sức khỏe',
          'Triển khai dịch vụ giao thuốc tận nhà miễn phí trong bán kính 2km, in số điện thoại Zalo rõ ràng lên túi đựng thuốc',
          'Tổ chức hoạt động cộng đồng định kỳ: ngày đo huyết áp và kiểm tra đường huyết miễn phí cho người cao tuổi',
          'Vận động 20 khách hàng thân thiết đánh giá 5 sao trên Google Maps để tăng uy tín hiển thị',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Luôn duy trì đầy đủ 50 mã sản phẩm thiết yếu khách hay tìm hỏi nhất — khách hỏi không có là mất khách vĩnh viễn',
          'Tập trung vào các nhóm hàng tạo lý do quay lại đều đặn hàng tháng: thuốc mạn tính, sữa dinh dưỡng, sản phẩm chăm sóc gia đình',
          'Lưu lại danh bạ và nhật ký khách mua thuốc mạn tính để chủ động gọi điện hỏi thăm và nhắc lịch tái mua',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Số lượt khách mỗi ngày — mục tiêu tăng thêm 5 lượt mỗi tháng',
          'Tỷ lệ khách hàng quay lại mua trong vòng 30 ngày',
          'Số lượt đánh giá mới trên Google Maps',
        ],
      },
    ],
  },
  {
    id: 'P7',
    ten: 'Quầy Kho Mỏng, Hay Đứt Hàng',
    tagline: 'Vốn nhẹ nhưng đang âm thầm đánh mất khách hàng',
    mota: 'Kho chỉ đủ bán trong {ngayTon} — rủi ro hết hàng và mất khách rất cao.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Kho hàng của bạn chỉ đủ bán trong {ngayTon}. Nghe qua thì có vẻ ít đọng vốn nhưng rủi ro tiềm ẩn rất lớn: khách ghé mua mà thiếu hàng sẽ lập tức chuyển sang quầy khác, mất đi thói quen trung thành. Thiệt hại này âm thầm diễn ra và không hiện lên báo cáo kế toán.',
        b: [
          'Thiếu vốn lưu động dự phòng nên chỉ nhập cầm chừng từng đơn nhỏ',
          'Quy trình đặt hàng thụ động: hết hàng mới gọi đặt thay vì đặt theo lịch dự báo cố định',
          'Vốn ít nhưng bị phân tán cho quá nhiều mã hàng thay vì tập trung bảo vệ các mã chủ lực',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Lập danh sách 50 mã sống còn không bao giờ được phép đứt hàng',
          'Xác định lượng bán trung bình mỗi tuần của từng mã và ấn định mức tồn tối thiểu tương đương 3 tuần bán',
          'Ghi nhận lại toàn bộ những lần khách hỏi mà không có hàng trong 7 ngày để thấy rõ mức độ mất doanh thu',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Đàm phán chính sách công nợ 15–30 ngày với nhà phân phối chính để giải quyết nút thắt vốn lưu động',
          'Thiết lập lịch kiểm tra và đặt hàng cố định 2 lần/tuần thay vì đợi quầy trống hàng mới gọi',
          'Ưu tiên hợp tác với các nhà phân phối có khả năng giao hàng nhanh trong ngày',
          'Thanh lý các mã chậm để thu hồi vốn dồn cho danh mục hàng bán chạy',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Tập trung bảo vệ tối đa 50 mã chủ lực trước, các mã đặc trị hiếm gặp có thể nhận đặt hàng theo yêu cầu',
          'Nguyên tắc: Thà đủ 100% cho 50 mã chạy còn hơn thiếu hụt đều ở 200 mã',
          'Chủ động hẹn giao tận nhà trong ngày cho khách nếu tạm hết hàng, giữ chân không để khách đi quầy khác',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Số lượt khách hỏi mà quầy thiếu hàng — mục tiêu đưa về 0',
          'Số ngày tồn kho an toàn — mục tiêu nâng lên 45–55 ngày',
          'Số mã chủ lực bị đứt hàng trong tuần',
        ],
      },
    ],
  },
  {
    id: 'P8',
    ten: 'Quầy Khỏe, Sẵn Sàng Mở Rộng',
    tagline: 'Bài toán lúc này là dùng dòng tiền để làm gì để tăng trưởng',
    mota: 'Tất cả 6 chỉ số đều nằm trong ngưỡng lành mạnh, lợi nhuận đạt {ln}/tháng ({bienLN}).',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Cả sáu chỉ số vận hành của quầy đều nằm trong ngưỡng lý tưởng của ngành, lợi nhuận đạt khoảng {ln} mỗi tháng, tương đương {bienLN} doanh thu. Ở trạng thái này, việc tối ưu cắt giảm thêm chi phí không mang lại nhiều ý nghĩa. Thách thức lớn nhất là quầy đang quá phụ thuộc vào sự hiện diện cá nhân của bạn.',
        b: [
          'Quầy kinh doanh tốt nhưng quy trình và bí quyết chủ yếu nằm trong đầu người chủ, chưa được văn bản hóa',
          'Dòng tiền dồi dào nhưng chưa có kế hoạch tái đầu tư rõ ràng nên dễ phân tán vào chi tiêu cá nhân',
          'Quy mô bán lẻ đã đủ lớn nhưng chưa đàm phán chính sách giá cấp 1 trực tiếp với các hãng dược',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Liệt kê danh sách các công việc mà hiện tại chỉ có bạn mới làm được — đây chính là rào cản ngăn bạn nhân bản cơ sở',
          'Tính toán chính xác dòng tiền tự do trong 6 tháng gần nhất để biết nguồn lực sẵn có cho đầu tư mở rộng',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Chọn 1 trong 3 chiến lược tăng trưởng: mở thêm điểm bán mới, mở rộng mảng dược mỹ phẩm/TPCN chuyên sâu, hoặc xây dựng kênh giao thuốc online',
          'Nếu chuẩn bị mở điểm mới: chuẩn hóa toàn bộ quy trình thành văn bản và đào tạo được 1 dược sĩ trưởng thay bạn quản lý điểm hiện tại',
          'Đàm phán lại chính sách giá với các nhà phân phối lớn dựa trên sản lượng thực tế đang bán',
          'Số hóa tệp khách hàng thân thiết kèm số điện thoại và lịch sử mua sắm',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Mở rộng chiều sâu vào các nhóm hàng biên lợi nhuận cao và có tần suất mua nhắc lại đều đặn',
          'Khi sản lượng một nhóm hàng đạt ngưỡng đủ lớn, cân nhắc phát triển nhãn hàng độc quyền hoặc liên kết độc quyền khu vực',
          'Xây dựng từ 3–5 giải pháp sức khỏe đặc trưng mà khách hàng chỉ có thể tìm thấy tại quầy của bạn',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Lợi nhuận ròng hàng tháng và số dư dòng tiền tự do',
          'Tỷ lệ doanh thu phát sinh khi người chủ không có mặt tại quầy',
          'Số lượng quy trình vận hành đã được chuẩn hóa và chuyển giao',
        ],
      },
    ],
  },
  {
    id: 'P9',
    ten: 'Quầy Quy Mô Chưa Đủ',
    tagline: 'Vận hành không tệ, nhưng doanh số chưa gánh nổi chi phí cố định',
    mota: 'Doanh thu {dt}/tháng còn quá mỏng so với {cpFix} chi phí cố định phải gánh.',
    hangmuc: [
      {
        t: 'Chẩn đoán nguyên nhân',
        p: 'Không có chỉ số nào của quầy bị hỏng nặng, vấn đề cốt lõi nằm ở quy mô doanh số: doanh thu {dt}/tháng còn mỏng so với tổng {cpFix} chi phí cố định đang gánh. Quầy cần đạt doanh số tối thiểu {hvNgay}/ngày mới chạm điểm hòa vốn, trong khi hiện tại đang bán {ds}. Đây là bài toán cấp bách phải tăng doanh thu, không thể chỉ dừng ở cắt giảm.',
        b: [
          'Bộ máy chi phí cố định được thiết lập cho một quy mô doanh số lớn hơn thực tế',
          'Quầy mới thành lập hoặc mới đổi địa điểm, chưa tích lũy đủ tệp khách quen trong khu vực',
          'Chưa bóc tách rõ doanh thu thấp do thiếu lượt khách ghé hay do giá trị trên mỗi đơn hàng còn nhỏ',
        ],
      },
      {
        t: 'Việc làm trong 7 ngày',
        p: '',
        b: [
          'Tính chính xác con số hòa vốn theo ngày là {hvNgay} và dán ngay cạnh máy tính tiền làm mục tiêu bắt buộc',
          'Rà soát toàn bộ chi phí cố định, loại bỏ ngay những khoản chi phí chưa thực sự cần thiết trong tháng',
          'Xác định rõ nguyên nhân chính: thiếu lượng khách ghé hay mỗi khách ghé mua quá ít',
        ],
      },
      {
        t: 'Việc làm trong 30 ngày',
        p: '',
        b: [
          'Nếu thiếu khách: đẩy mạnh Google Maps, nhóm Zalo khu dân cư, triển khai phát tờ rơi và giao hàng tận nhà',
          'Nếu khách mua ít: tập trung đào tạo bán kèm và tư vấn đủ liệu trình điều trị',
          'Đặt mốc giới hạn 90 ngày: phải chạm mốc hòa vốn. Nếu không đạt cần xem xét lại phương án mặt bằng hoặc chuyển nhượng mô hình',
          'Ưu tiên bảo toàn dòng tiền mặt thay vì cố gắng dàn trải danh mục hàng hóa',
        ],
      },
      {
        t: 'Cơ cấu hàng và chính sách bán',
        p: '',
        b: [
          'Chưa mở rộng danh mục dàn trải trong giai đoạn này; tập trung dồn vốn cho 50 mã bán chạy nhất',
          'Chọn 3 nhóm vấn đề sức khỏe phổ biến nhất trong khu dân cư và phục vụ thật xuất sắc',
          'Nguyên tắc quản trị vốn: mọi đồng vốn nhập hàng phải quay vòng được trong vòng tối đa 45 ngày',
        ],
      },
      {
        t: 'Chỉ số theo dõi hàng tuần',
        p: '',
        b: [
          'Doanh thu thực tế mỗi ngày so với mốc hòa vốn {hvNgay}',
          'Số ngày trong tháng đạt vượt ngưỡng hòa vốn',
          'Số dư tiền mặt thực tế vào cuối mỗi tuần',
        ],
      },
    ],
  },
];

// Hàm nội suy tuyến tính tính điểm
function interpolateScore(x, pts) {
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    if (x <= pts[i][0]) {
      const a = pts[i - 1];
      const b = pts[i];
      const t = (x - a[0]) / (b[0] - a[0] || 1);
      return a[1] + t * (b[1] - a[1]);
    }
  }
  return pts[pts.length - 1][1];
}

export function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export function fmtNumber(n, decimals = 1) {
  if (!isFinite(n) || isNaN(n)) return '—';
  return n.toLocaleString('vi-VN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtTrieu(n, decimals = 1) {
  return `${fmtNumber(n, decimals)} tr`;
}

export function fmtPercent(n, decimals = 1) {
  return `${fmtNumber(n, decimals)}%`;
}

/**
 * Động cơ tính toán tài chính & chỉ số sức khỏe quầy
 */
export function computeHealthMetrics(inputs) {
  const d = {
    ds: Math.max(0, parseFloat(inputs.ds) || 0),
    ngay: 30,
    bien: clamp(parseFloat(inputs.bien) || 0, 0, 90),
    dongia: Math.max(1, parseFloat(inputs.dongia) || 1),
    mb: Math.max(0, parseFloat(inputs.mb) || 0),
    khac: Math.max(0, parseFloat(inputs.khac) || 0),
    ns: Math.max(0, parseFloat(inputs.ns) || 0),
    ton: Math.max(0, parseFloat(inputs.ton) || 0),
    nhansu: Math.max(1, parseFloat(inputs.nhansu) || 1),
    huy: clamp(parseFloat(inputs.huy) || 0.8, 0, 20),
    vay: Math.max(0, parseFloat(inputs.vay) || 0),
    namhd: Math.max(0, parseFloat(inputs.namhd) || 0),
    vitri: inputs.vitri || 'dancu',
  };

  const r = {};
  r.dt = d.ds * d.ngay;                          // Doanh thu tháng (triệu)
  r.laiGop = (r.dt * d.bien) / 100;              // Lãi gộp (triệu)
  r.giaVon = r.dt - r.laiGop;                    // Giá vốn hàng bán (triệu)
  r.cpCoDinh = d.mb + d.khac;                    // Chi phí cố định mặt bằng + khác
  r.cpNS = d.ns;                                 // Chi phí nhân sự (đã tính công chủ)
  r.huyTien = (r.dt * d.huy) / 100;              // Hao hụt cận date / hỏng / hủy
  r.tongCP = r.cpCoDinh + r.cpNS + r.huyTien + d.vay; // Tổng chi phí hoạt động
  r.ln = r.laiGop - r.tongCP;                    // Lợi nhuận ròng tháng (triệu)
  r.bienLN = r.dt > 0 ? (r.ln / r.dt) * 100 : 0; // Biên lợi nhuận ròng (%)

  r.contrib = (d.bien - d.huy) / 100;            // Biên đóng góp
  r.cpCoDinhTong = r.cpCoDinh + r.cpNS + d.vay;  // Tổng chi phí cố định bắt buộc
  r.dtHoaVon = r.contrib > 0.005 ? r.cpCoDinhTong / r.contrib : Infinity; // Doanh thu hòa vốn tháng
  r.hvNgay = isFinite(r.dtHoaVon) ? r.dtHoaVon / d.ngay : Infinity;      // Doanh thu hòa vốn ngày
  r.dem = (r.dt > 0 && isFinite(r.dtHoaVon)) ? ((r.dt - r.dtHoaVon) / r.dt) * 100 : -100; // Đệm an toàn (%)

  r.giaVonNgay = r.giaVon / 30;                  // Giá vốn bình quân ngày
  r.ngayTon = r.giaVonNgay > 0 ? d.ton / r.giaVonNgay : (d.ton > 0 ? 999 : 0); // Số ngày tồn kho
  r.vongQuay = r.ngayTon > 0 ? 365 / r.ngayTon : 0; // Số vòng quay tồn kho/năm

  r.luot = (d.ds * 1000) / d.dongia;             // Lượt khách mỗi ngày
  r.dtNS = r.dt / d.nhansu;                      // Doanh thu trên mỗi nhân sự/tháng
  r.tyMB = r.dt > 0 ? (d.mb / r.dt) * 100 : 0;  // Tỷ lệ tiền thuê / doanh thu (%)
  r.tyNS = r.dt > 0 ? (r.cpNS / r.dt) * 100 : 0; // Tỷ lệ nhân sự / doanh thu (%)
  r.tyVanHanh = r.dt > 0 ? ((r.cpCoDinh + r.cpNS) / r.dt) * 100 : 0; // Tỷ lệ vận hành / doanh thu (%)
  r.vonLuuDong = d.ton + r.tongCP;               // Vốn lưu động ước tính
  r.roi = r.vonLuuDong > 0 ? ((r.ln * 12) / r.vonLuuDong) * 100 : 0; // Tỷ suất sinh lời vốn lưu động (%)

  // Tính điểm 6 trục (Tổng 100 điểm)
  const s = {};
  s.ln = interpolateScore(r.bienLN, [
    [-10, 0], [0, 3], [2, 7], [4, 12], [6, 17], [8, 21], [10, 24], [14, 25],
  ]);
  s.gop = interpolateScore(d.bien, [
    [8, 0], [12, 3], [16, 7], [19, 11], [22, 13.5], [26, 15], [40, 15],
  ]);
  s.ton = interpolateScore(r.ngayTon, [
    [0, 4], [20, 10], [32, 17], [42, 20], [65, 20], [85, 14], [110, 8], [150, 2], [250, 0],
  ]);
  s.cp = interpolateScore(r.tyVanHanh, [
    [6, 15], [10, 14], [14, 11.5], [18, 8], [22, 5], [28, 1.5], [40, 0],
  ]);
  s.dem = interpolateScore(r.dem, [
    [-30, 0], [0, 1], [8, 5], [16, 8.5], [26, 11.5], [38, 14], [50, 15],
  ]);
  const sNS = interpolateScore(r.dtNS, [
    [30, 0], [60, 1.5], [90, 3], [130, 4.2], [190, 5], [300, 5],
  ]);
  const sDG = interpolateScore(d.dongia, [
    [35, 0.5], [55, 1.5], [80, 3], [110, 4.2], [160, 5], [300, 5],
  ]);
  s.nangsuat = sNS + sDG;

  r.scores = s;
  let total = s.ln + s.gop + s.ton + s.cp + s.dem + s.nangsuat;

  // "Số ngày tồn kho" tính theo giá vốn (ton / giá vốn mỗi ngày) - đúng chuẩn kế toán, nhưng
  // ở nhóm hàng biên lãi gộp rất cao (dược mỹ phẩm, TPCN, thiết bị y tế... 50-90%), giá vốn/ngày
  // co lại rất nhỏ nên cùng 1 giá trị tồn kho sẽ bị quy đổi thành "rất nhiều ngày" một cách ảo,
  // dù tồn kho đó thực ra chỉ tương đương vài tháng doanh thu bình thường. Vì vậy chỉ coi tồn kho
  // là "trục hỏng nặng" khi VỪA có điểm thấp VỪA thực sự lớn so với doanh thu (không phải do biên
  // lãi gộp cao làm méo con số) - tránh ghim "Cảnh báo" oan cho quầy lãi cao thật sự khỏe mạnh.
  const tonKhoThatSuNang = s.ton < 5 && (r.dt <= 0 || d.ton / r.dt > 3);

  // Một trục hỏng nặng thì không thể xếp loại Khá
  if (tonKhoThatSuNang || s.dem < 2 || s.ln < 4) {
    total = Math.min(total, 66);
  }
  // Không tính công chủ quầy thì chặn tối đa 55 điểm
  r.noSalary = (d.ns === 0);
  if (r.noSalary) {
    total = Math.min(total, 55);
  }

  r.total = Math.round(clamp(total, 0, 100));
  r.d = d;

  // Xếp band
  r.band = HEALTH_BANDS.find((b) => r.total >= b.min) || HEALTH_BANDS[HEALTH_BANDS.length - 1];

  return r;
}

/**
 * Chọn chân dung quầy phù hợp nhất
 */
export function pickPortrait(r) {
  if (r.noSalary) {
    const p0 = PORTRAITS.find((p) => p.id === 'P0');
    return { portrait: p0, sev: 999 };
  }

  const c = [];
  const add = (id, sev) => {
    if (sev > 0) c.push({ id, sev });
  };

  if (r.tyMB > 6.5) add('P1', (r.tyMB - 5.5) * 12);
  if (r.ngayTon > 72) add('P2', (r.ngayTon - 65) * 1.1);
  if (r.d.bien < 19) add('P3', (20 - r.d.bien) * 13);
  if (r.tyNS > 12 || (r.dtNS < 85 && r.tyNS > 10)) {
    add('P4', Math.max((r.tyNS - 11) * 11, (85 - r.dtNS) * 0.6));
  }
  if (r.d.dongia < 75) add('P5', (80 - r.d.dongia) * 1.1);
  if (r.luot < 40) add('P6', (42 - r.luot) * 2.0);
  if (r.ngayTon < 28 && r.d.ton > 0) add('P7', (28 - r.ngayTon) * 2.5);

  if (c.length === 0) {
    const targetId = (r.ln < 0 || r.total < 70) ? 'P9' : 'P8';
    const p = PORTRAITS.find((item) => item.id === targetId) || PORTRAITS[0];
    return { portrait: p, sev: 0 };
  }

  c.sort((a, b) => b.sev - a.sev);
  const matched = PORTRAITS.find((item) => item.id === c[0].id) || PORTRAITS[0];
  return { portrait: matched, sev: c[0].sev };
}

/**
 * Điền token vào văn bản nội dung của Portrait
 */
export function fillPortraitTokens(str, r) {
  const t = {
    tyMB: fmtPercent(r.tyMB),
    tyNS: fmtPercent(r.tyNS),
    ngayTon: `${Math.round(r.ngayTon)} ngày`,
    bien: fmtPercent(r.d.bien),
    bienDon: fmtNumber(r.d.bien * 1000, 0),
    dongia: `${fmtNumber(r.d.dongia, 0)}.000đ`,
    luot: Math.round(r.luot),
    dtNS: `${fmtNumber(r.dtNS, 0)} triệu`,
    ln: `${fmtNumber(Math.abs(r.ln), 1)} triệu`,
    bienLN: fmtPercent(r.bienLN),
    dt: `${fmtNumber(r.dt, 0)} triệu`,
    cpFix: `${fmtNumber(r.cpCoDinhTong, 1)} triệu`,
    hvNgay: isFinite(r.hvNgay) ? `${fmtNumber(r.hvNgay, 1)} triệu` : '—',
    ds: `${fmtNumber(r.d.ds, 1)} triệu`,
    tienChet: `${fmtNumber(Math.max(0, r.d.ton - 65 * r.giaVonNgay), 0)} triệu`,
  };

  return String(str || '').replace(/\{(\w+)\}/g, (match, key) => {
    return t[key] !== undefined ? t[key] : match;
  });
}

/**
 * 4 Đòn bẩy tài chính quan trọng nhất
 */
export function getLevers(r) {
  const levers = [
    {
      name: 'Nâng biên lãi gộp thêm 1 điểm %',
      desc: 'Đổi cơ cấu hàng, đàm phán giá nhập, tăng tỷ trọng hàng độc quyền và TPCN',
      val: r.dt * 0.01,
      unit: 'tr/tháng',
    },
    {
      name: 'Tăng đơn giá trung bình thêm 10.000đ',
      desc: 'Bán kèm đúng bệnh lý, tư vấn đủ liệu trình thay vì bán lẻ từng vỉ',
      val: (r.luot * 10 / 1000) * r.d.ngay * r.contrib,
      unit: 'tr/tháng',
    },
    {
      name: 'Cắt giảm 5% chi phí cố định & nhân sự',
      desc: 'Đàm phán lại tiền thuê, rà soát phần mềm và dịch vụ đang trả mà ít dùng',
      val: (r.cpCoDinh + r.cpNS) * 0.05,
      unit: 'tr/tháng',
    },
    {
      name: 'Thêm 5 lượt khách mỗi ngày',
      desc: 'Google Maps, làm sáng biển hiệu, Zalo cư dân khu phố, nhận ship 2km',
      val: 5 * (r.d.dongia / 1000) * r.d.ngay * r.contrib,
      unit: 'tr/tháng',
    },
    {
      name: 'Giảm hao hụt cận date một nửa',
      desc: 'Kiểm date định kỳ, chủ động đẩy hàng gần hạn bằng combo trước 90 ngày',
      val: r.huyTien * 0.5,
      unit: 'tr/tháng',
    },
  ];

  levers.sort((a, b) => b.val - a.val);
  const top4 = levers.slice(0, 4);

  // Bonus đòn bẩy giải phóng dòng tiền từ kho
  const cashTraiVe = Math.max(0, r.d.ton - 60 * r.giaVonNgay);

  return {
    top4,
    cashTraiVe: cashTraiVe > 1 ? cashTraiVe : null,
  };
}

/**
 * Cảnh báo tài chính động
 */
export function getAlerts(r) {
  const alerts = [];

  if (r.ln < 0) {
    alerts.push({
      type: 'bad',
      icon: '!',
      title: `Quầy đang lỗ khoảng ${fmtTrieu(Math.abs(r.ln))}/tháng`,
      desc: 'Sau khi trừ giá vốn, mặt bằng, nhân sự (đã tính công chủ) và hao hụt, quầy còn âm. Mỗi tháng hoạt động là đang lấy vốn dự trữ bù vào.',
    });
  }

  if (r.d.ns === 0) {
    alerts.push({
      type: 'warn',
      icon: '!',
      title: 'Bạn chưa tính tiền công cho chính mình',
      desc: 'Đang để chi phí nhân sự bằng 0. Công sức đứng quầy của bạn là chi phí thật — hãy cộng mức lương bạn phải trả nếu thuê người làm thay để biết lợi nhuận chuẩn.',
    });
  }

  if (r.ngayTon > 90) {
    alerts.push({
      type: 'bad',
      icon: '₫',
      title: `Khoảng ${fmtTrieu(Math.max(0, r.d.ton - 65 * r.giaVonNgay))} đang bị giam chết trong kho`,
      desc: `Kho đủ bán ${Math.round(r.ngayTon)} ngày, trong khi ngưỡng an toàn là 45–65 ngày. Phần tồn đọng vừa chiếm vốn lưu động, vừa tiềm ẩn nguy cơ hàng cận date.`,
    });
  } else if (r.ngayTon > 70) {
    alerts.push({
      type: 'warn',
      icon: '₫',
      title: `Tồn kho hơi nặng: ${Math.round(r.ngayTon)} ngày`,
      desc: 'Chưa nguy hiểm nhưng đang giữ tiền không cần thiết. Hãy rà soát 20 mã chậm nhất, tạm ngừng nhập thêm và bán xả qua combo.',
    });
  } else if (r.ngayTon < 25 && r.d.ton > 0) {
    alerts.push({
      type: 'warn',
      icon: '!',
      title: `Tồn kho quá mỏng: ${Math.round(r.ngayTon)} ngày`,
      desc: 'Rủi ro đứt hàng chủ lực rất cao. Khách hỏi mà không có hàng là mất đơn và mất luôn thói quen quay lại quầy.',
    });
  }

  if (r.tyMB > 8) {
    alerts.push({
      type: 'bad',
      icon: '%',
      title: `Tiền thuê mặt bằng chiếm ${fmtPercent(r.tyMB)} doanh thu`,
      desc: 'Ngưỡng an toàn của quầy lẻ là dưới 6%. Ở mức này, gần như toàn bộ tuần đầu mỗi tháng bạn bán hàng chỉ để trả tiền nhà.',
    });
  }

  if (r.tyNS > 15) {
    alerts.push({
      type: 'warn',
      icon: '%',
      title: `Chi phí nhân sự chiếm ${fmtPercent(r.tyNS)} doanh thu`,
      desc: `Mỗi nhân sự chỉ tạo ra ${fmtTrieu(r.dtNS)} doanh thu/tháng. Ngưỡng khỏe là trên 120 triệu. Nên tập trung nâng cao năng suất tư vấn.`,
    });
  }

  if (r.d.bien < 17) {
    alerts.push({
      type: 'warn',
      icon: '%',
      title: `Biên lãi gộp chỉ đạt ${fmtPercent(r.d.bien)}`,
      desc: `Quầy phụ thuộc nhiều vào thuốc phổ thông cạnh tranh giá rẻ. Chỉ cần nâng biên thêm 2 điểm % là có thêm ${fmtTrieu(r.dt * 0.02)} lợi nhuận mỗi tháng.`,
    });
  }

  if (r.dem < 12 && isFinite(r.dem)) {
    alerts.push({
      type: 'bad',
      icon: '!',
      title: `Đệm an toàn hòa vốn chỉ còn ${fmtPercent(Math.max(0, r.dem))}`,
      desc: 'Doanh số chỉ cần sụt giảm nhẹ là quầy rơi vào tình trạng lỗ ngay. Rất nhạy cảm với mùa vắng khách hoặc các đợt phát sinh chi phí.',
    });
  }

  if (r.luot < 30) {
    alerts.push({
      type: 'warn',
      icon: '#',
      title: `Chỉ khoảng ${Math.round(r.luot)} lượt khách mỗi ngày`,
      desc: 'Lưu lượng khách thấp. Trước khi nghĩ đến giảm giá, hãy kiểm tra biển hiệu, giờ mở cửa, hiện diện Google Maps và nhóm Zalo cư dân.',
    });
  }

  if (r.d.vay > 0 && r.d.vay > r.ln && r.ln > 0) {
    alerts.push({
      type: 'bad',
      icon: '!',
      title: 'Chi phí lãi vay đang ăn mòn gần hết lợi nhuận',
      desc: `Trả lãi ${fmtTrieu(r.d.vay)}/tháng trong khi lợi nhuận thực chỉ còn ${fmtTrieu(r.ln)}. Cần ưu tiên thu hồi vốn tồn kho để giảm dư nợ vay.`,
    });
  }

  if (alerts.length === 0) {
    alerts.push({
      type: 'good',
      icon: '✓',
      title: 'Không có cảnh báo nghiêm trọng nào',
      desc: 'Cả 6 chỉ số đều nằm trong ngưỡng an toàn lành mạnh. Bước tiếp theo là tập trung mở rộng quy mô hoặc đưa thêm nhóm hàng biên cao.',
    });
  }

  return alerts.slice(0, 6);
}

/**
 * Dữ liệu Bảng Lãi Lỗ (P&L) 1 tháng
 */
export function getPnlData(r) {
  const rows = [
    { title: 'Doanh thu bán hàng', val: r.dt, isSum: false, isNegative: false },
    { title: 'Giá vốn hàng bán (COGS)', val: r.giaVon, isSum: false, isNegative: true },
    { title: 'Lãi gộp (Gross Profit)', val: r.laiGop, isSum: true, isHighlight: true, isNegative: false },
    { title: 'Thuê mặt bằng', val: r.d.mb, isSum: false, isNegative: true },
    { title: 'Chi phí cố định khác (điện, nước, phần mềm...)', val: r.d.khac, isSum: false, isNegative: true },
    { title: 'Lương & thưởng nhân sự (gồm công chủ)', val: r.cpNS, isSum: false, isNegative: true },
    { title: 'Hao hụt, cận date, hỏng, hủy', val: r.huyTien, isSum: false, isNegative: true },
  ];

  if (r.d.vay > 0) {
    rows.push({ title: 'Chi phí lãi vay', val: r.d.vay, isSum: false, isNegative: true });
  }

  rows.push({
    title: 'Lợi nhuận ròng còn lại (Net Profit)',
    val: r.ln,
    isSum: true,
    isFinal: true,
    isNegative: r.ln < 0,
  });

  rows.push({
    title: 'Doanh số hòa vốn bắt buộc mỗi ngày',
    val: isFinite(r.hvNgay) ? r.hvNgay : null,
    isSum: false,
    isBreakEven: true,
  });

  return rows;
}

/**
 * 6 Chỉ số Benchmark ngành bán lẻ dược phẩm
 */
export function getBenchmarkMetrics(r) {
  return [
    {
      id: 'bienLN',
      name: 'Biên lợi nhuận ròng',
      val: fmtPercent(r.bienLN),
      pos: clamp(((r.bienLN + 6) / 22) * 100, 0, 100),
      zones: [
        { width: 27, color: '#ef4444' }, // Lỗ / Âm
        { width: 18, color: '#f59e0b' }, // 0 - 4%
        { width: 55, color: '#10b981' }, // 4% - 16%+
      ],
      labels: ['-6%', '0%', '4%', '+16%'],
      note:
        r.bienLN >= 8
          ? `Rất tốt. Cứ 100.000đ doanh thu, bạn giữ lại được ${fmtNumber(r.bienLN * 1000, 0)}đ sau khi thanh toán đầy đủ mọi chi phí.`
          : r.bienLN >= 4
          ? 'Mức chấp nhận được nhưng chưa thực sự dày. Quầy bán lẻ khỏe thường đạt từ 8% trở lên.'
          : r.bienLN >= 0
          ? 'Quá mỏng. Chỉ một biến động nhỏ về doanh thu hoặc tiền thuê tăng là quầy rơi vào lỗ.'
          : 'Đang âm lợi nhuận. Càng bán nhiều với cấu trúc này thì càng lấy vốn ra bù.',
    },
    {
      id: 'bienGop',
      name: 'Biên lãi gộp trung bình',
      val: fmtPercent(r.d.bien),
      pos: clamp((r.d.bien / 36) * 100, 0, 100),
      zones: [
        { width: 44, color: '#ef4444' }, // 0 - 16%
        { width: 17, color: '#f59e0b' }, // 16 - 22%
        { width: 39, color: '#10b981' }, // 22 - 36%
      ],
      labels: ['0%', '16%', '22%', '36%'],
      note:
        r.d.bien >= 22
          ? 'Cơ cấu hàng tốt — bạn không chỉ bán thuốc thông thường mà đã có các dòng sản phẩm tạo biên lợi nhuận.'
          : r.d.bien >= 18
          ? 'Mức phổ biến của quầy thuốc lẻ. Nên tăng thêm 2–3 điểm % qua dòng TPCN, dược mỹ phẩm, hàng độc quyền.'
          : 'Mỏng so với chuẩn ngành. Quầy đang bán nhiều mã phổ thông cạnh tranh giá, hoặc chưa có giá nhập tốt.',
    },
    {
      id: 'ngayTon',
      name: 'Số ngày tồn kho bình quân',
      val: `${Math.round(r.ngayTon)} ngày`,
      pos: clamp((r.ngayTon / 150) * 100, 0, 100),
      zones: [
        { width: 17, color: '#f59e0b' }, // < 25 ngày: mỏng
        { width: 26, color: '#10b981' }, // 25 - 65 ngày: chuẩn
        { width: 17, color: '#f59e0b' }, // 65 - 90 ngày: hơi nặng
        { width: 40, color: '#ef4444' }, // > 90 ngày: nguy cơ chết vốn
      ],
      labels: ['0', '25', '65', '90', '150+'],
      note:
        r.ngayTon > 90
          ? `Đọng vốn trong kho. Khoảng ${fmtTrieu(Math.max(0, r.d.ton - 65 * r.giaVonNgay))} tiền mặt có thể giải phóng nếu kéo tồn kho về chuẩn.`
          : r.ngayTon > 65
          ? 'Tồn kho hơi dày. Kéo về 55–60 ngày sẽ giúp bạn có thêm tiền mặt xoay vòng mà vẫn đủ hàng bán.'
          : r.ngayTon < 25
          ? 'Kho quá mỏng. Rủi ro đứt hàng cao, khách hỏi không có dễ bỏ sang quầy lân cận.'
          : 'Vòng quay tồn kho rất khỏe. Hàng vào ra đều đặn, giảm thiểu tối đa rủi ro cận date.',
    },
    {
      id: 'tyVanHanh',
      name: 'Chi phí vận hành trên doanh thu',
      val: fmtPercent(r.tyVanHanh),
      pos: clamp((r.tyVanHanh / 30) * 100, 0, 100),
      zones: [
        { width: 33, color: '#10b981' }, // < 10%
        { width: 27, color: '#f59e0b' }, // 10 - 18%
        { width: 40, color: '#ef4444' }, // > 18%
      ],
      labels: ['0%', '10%', '18%', '30%+'],
      note: `Mặt bằng chiếm ${fmtPercent(r.tyMB)} · Nhân sự chiếm ${fmtPercent(r.tyNS)} doanh thu. ${
        r.tyMB > 8
          ? 'Tiền thuê đang ăn quá sâu vào lãi gộp quầy.'
          : r.tyNS > 14
          ? 'Chi phí nhân sự cao so với doanh số tạo ra.'
          : 'Cấu trúc chi phí đang trong ngưỡng an toàn lành mạnh.'
      }`,
    },
    {
      id: 'demAnToan',
      name: 'Đệm an toàn trên điểm hòa vốn',
      val: isFinite(r.dem) ? fmtPercent(r.dem) : '—',
      pos: clamp(((r.dem + 20) / 60) * 100, 0, 100),
      zones: [
        { width: 33, color: '#ef4444' }, // < 0%
        { width: 17, color: '#f59e0b' }, // 0 - 10%
        { width: 50, color: '#10b981' }, // > 10%
      ],
      labels: ['-20%', '0%', '+10%', '+40%'],
      note: isFinite(r.dtHoaVon)
        ? `Quầy hòa vốn ở mức ${fmtTrieu(r.hvNgay)}/ngày. Doanh số có thể giảm tối đa ${fmtPercent(Math.max(0, r.dem))} trước khi bắt đầu lỗ.`
        : 'Không tính được điểm hòa vốn do biên lãi gộp chưa đủ bù đắp chi phí biến đổi.',
    },
    {
      id: 'nangSuat',
      name: 'Năng suất bán hàng & Đơn giá',
      val: `${Math.round(r.luot)} lượt/ngày`,
      pos: clamp((r.d.dongia / 180) * 100, 0, 100),
      zones: [
        { width: 31, color: '#ef4444' }, // < 55k
        { width: 30, color: '#f59e0b' }, // 55k - 110k
        { width: 39, color: '#10b981' }, // > 110k
      ],
      labels: ['0k', '55k', '110k', '180k'],
      note: `Đơn giá TB ${fmtNumber(r.d.dongia, 0)}.000đ · Doanh thu ${fmtTrieu(r.dtNS)}/người/tháng. ${
        r.d.dongia < 75
          ? 'Đơn giá còn thấp — cơ hội bứt phá lớn nhất là tư vấn kèm liệu trình và giải pháp chăm sóc đi cùng.'
          : 'Đơn giá tốt, kỹ năng tư vấn cắt liều và bán kèm của dược sĩ đang phát huy tác dụng.'
      }`,
    },
  ];
}
